import * as React from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Input = React.forwardRef(
  ({ className, type, clearable = false, onClear, onChange, onKeyDown, onFocus, onBlur, value, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const decimalIntentRef = React.useRef(false);
    const supportsClearType =
      type === undefined || type === "text" || type === "search";
    const isClearable = clearable && supportsClearType;
    const hasValue = value !== undefined && value !== null && String(value).length > 0;
    const canClear = isClearable && !props.disabled && hasValue;

    const assignRef = (el) => {
      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    };

    const clearValue = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (onClear) {
        onClear();
        return;
      }
      onChange?.({ target: { value: "" } });
    };

    const handleNumberLeadingZeroReplace = (e) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (type !== "number") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const current = value ?? e.currentTarget?.value ?? "";
      const text = String(current ?? "");
      const start = e.currentTarget?.selectionStart ?? text.length;
      const end = e.currentTarget?.selectionEnd ?? text.length;
      const atTail = start === text.length && end === text.length;

      // Preserve decimal workflow: typing "0", then ".", then digits should produce "0.xx".
      if ((e.key === "." || e.key === "Decimal") && text === "0" && atTail) {
        decimalIntentRef.current = true;
        return;
      }

      if (!/^\d$/.test(e.key)) return;
      if (decimalIntentRef.current) {
        decimalIntentRef.current = false;
        return;
      }

      // If field is "0", first numeric key replaces it instead of appending.
      if (text === "0" && atTail) {
        e.preventDefault();
        onChange?.({
          target: { value: e.key, name: props.name },
          currentTarget: { value: e.key, name: props.name },
        });
      }
    };

    const handleFocus = (e) => {
      setIsFocused(true);
      onFocus?.(e);
      if (e.defaultPrevented) return;
      if (type !== "number") return;
      if (String(value ?? "") === "0") {
        try {
          e.currentTarget.select();
        } catch {}
      }
    };

    const handleBlur = (e) => {
      setIsFocused(false);
      decimalIntentRef.current = false;
      onBlur?.(e);
      if (e.defaultPrevented) return;
      if (type !== "number") return;
      if (String(e.currentTarget?.value ?? "").trim() !== "") return;
      onChange?.({
        target: { value: "0", name: props.name },
        currentTarget: { value: "0", name: props.name },
      });
    };

    const renderedValue =
      type === "number" && !isFocused && (value === "" || value === null || value === undefined)
        ? "0"
        : value;

    const inputEl = (
      <input
        type={type}
        className={cn(
          "flex h-9 max-[430px]:h-8 w-full min-w-0 rounded-md border border-input bg-transparent px-3 max-[430px]:px-2.5 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          isClearable && "pr-9",
          className
        )}
        ref={assignRef}
        value={renderedValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleNumberLeadingZeroReplace}
        onChange={onChange}
        {...props}
      />
    );

    if (!isClearable) return inputEl;

    return (
      <div className="relative w-full">
        {inputEl}
        {canClear ? (
          <button
            type="button"
            onClick={clearValue}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear input"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
    );
  }
)
Input.displayName = "Input"

export { Input }

